import React from 'react';
import { Link } from 'react-router-dom';
import { ActionButton, StatusBadge } from '../../../../../components/ui';
import StatusDropdown from './StatusDropdown';
import LeadScoreGauge from './LeadScoreGauge';
import { getCountryDisplayName } from '../constants';

/**
 * Submission Header Component (Redesigned)
 * Clean, organized header with lead info, status, and quick actions
 */
const SubmissionHeader = ({
    submission,
    opportunityScore = 0,
    onContactClick,
    onAddActivityClick,
    onSmsClick,
    onStatusChange,
    smsLoading = false,
    className = ''
}) => {
    if (!submission) return null;

    const fullName = [submission.first_name, submission.last_name].filter(Boolean).join(' ') || 'Unknown Lead';
    const country = getCountryDisplayName(submission.country_selection?.selectedCountry);
    const hasCv = !!submission.cv_data?.publicUrl;

    return (
        <div className={`bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl ${className}`}>
            {/* Main Header Content */}
            <div className="p-6">
                {/* Row 1: Navigation + Name + Badges */}
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4">
                        {/* Back Button */}
                        <Link
                            to="/admin/submissions"
                            className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-700/50 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>

                        {/* Name and Status */}
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h1 className="text-2xl font-bold text-white">{fullName}</h1>
                                <StatusBadge status={submission.status || 'pending'} size="sm" />
                                {opportunityScore >= 70 && (
                                    <span className="px-2 py-0.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                        🔥 HOT LEAD
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-400">
                                ID: <code className="text-gray-500 text-xs">{submission.id?.slice(0, 8)}...</code>
                            </p>
                        </div>
                    </div>

                    {/* Lead Score Gauge */}
                    <LeadScoreGauge value={opportunityScore} size="md" />
                </div>

                {/* Row 2: Contact Info */}
                <div className="flex flex-wrap items-center gap-6 mb-5 text-sm text-gray-400">
                    <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {submission.email || 'No email'}
                    </span>
                    <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {submission.phone || 'No phone'}
                    </span>
                    <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {country}
                    </span>
                    <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(submission.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        })}
                    </span>
                </div>

                {/* Row 3: Actions */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-700">
                    {/* Left: Status Dropdown + CV Button */}
                    <div className="flex items-center gap-3">
                        <StatusDropdown
                            value={submission.status || 'pending'}
                            onChange={onStatusChange}
                        />

                        {hasCv ? (
                            <a
                                href={submission.cv_data.publicUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-blue-900/30 hover:bg-blue-800/40 text-blue-300 text-sm rounded-lg border border-blue-700 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                View CV
                            </a>
                        ) : (
                            <span className="flex items-center gap-2 px-3 py-2 bg-yellow-900/20 text-yellow-400 text-sm rounded-lg border border-yellow-700/50">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                No CV
                            </span>
                        )}
                    </div>

                    {/* Right: Action Buttons */}
                    <div className="flex items-center gap-2">
                        <ActionButton
                            variant="primary"
                            size="sm"
                            onClick={onContactClick}
                            icon="📞"
                        >
                            Contact
                        </ActionButton>

                        <ActionButton
                            variant="secondary"
                            size="sm"
                            onClick={onAddActivityClick}
                            icon="➕"
                        >
                            Add Activity
                        </ActionButton>

                        {submission.phone && (
                            <ActionButton
                                variant="secondary"
                                size="sm"
                                onClick={onSmsClick}
                                disabled={smsLoading}
                                icon={smsLoading ? '⏳' : '📱'}
                            >
                                SMS
                            </ActionButton>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubmissionHeader;
